import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useEffect, useState } from "react";
import { api } from "../api";

const LOGO_URL = process.env.PUBLIC_URL + "/logo.png";

const ADMIN_NAV = [
  { to: "/admin",           label: "Dashboard",        icon: "▦" },
  { to: "/admin/customers", label: "Customer Accounts", icon: "◯" },
  { to: "/admin/disputes",  label: "Disputes",          icon: "⚑", badgeKey: "pending_disputes" },
  { to: "/admin/tickets",   label: "Ticket Requests",   icon: "◻", badgeKey: "pending_ticket_requests" },
];

export function Sidebar({ adminStats }) {
  const { email, logout, adminUser, adminLogout, isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreview = localStorage.getItem("ar_preview_mode") === "true";
  const showAdminNav = isAdmin && !isPreview;
  // roles[] is authoritative; the `role` column is a derived mirror, never read.
  const userRoles    = Array.isArray(adminUser?.roles) ? adminUser.roles : [];
  const isSuperAdmin    = userRoles.includes("super_admin");

  const [jobs, setJobs] = useState([]);
  const [jobsOpen, setJobsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    if (isAuthenticated && (!isAdmin || isPreview)) {
      api.getJobs().then(j => setJobs(j)).catch(() => {});
    }
  }, [isAuthenticated, isAdmin]);

  useEffect(() => {
    if (location.pathname === "/invoices" || location.pathname.startsWith("/invoices/")) {
      if (jobs.length > 0) setJobsOpen(true);
    }
  }, [location.pathname, jobs.length]);

  function handleLogout() {
    if (isPreview) {
      localStorage.removeItem("ar_preview_mode");
      localStorage.removeItem("ar_preview_customer");
      localStorage.removeItem("ar_token");
      localStorage.removeItem("ar_email");
      window.close();
    } else if (isAdmin) {
      adminLogout(); navigate("/admin/login");
    } else {
      logout(); navigate("/login");
    }
  }

  function handleJobClick(job) {
    if (selectedJob?.id === job.id) {
      setSelectedJob(null);
      navigate("/invoices");
    } else {
      setSelectedJob(job);
      navigate(`/invoices?job_id=${job.id}&job_name=${encodeURIComponent(job.customer_name)}`, { replace: false });
    }
  }

  function handleAllInvoices() {
    setSelectedJob(null);
    navigate("/invoices", { replace: false });
  }

  const displayEmail = isAdmin ? adminUser?.email : email;
  const displayName  = isAdmin ? adminUser?.name  : null;

  function getRoleBadge(roles) {
    const r = Array.isArray(roles) ? roles : (roles ? [roles] : []);
    if (!r.length) return null;
    const primary = r.includes("super_admin") ? "super_admin" : r[0];
    const label = primary === "super_admin" ? "Super Admin" : primary === "billing" ? "Billing" : primary === "qc" ? "QC" : primary;
    const cls   = primary === "super_admin" ? "super" : primary === "billing" ? "billing" : primary === "qc" ? "qc" : "";
    return <span className={`admin-badge ${cls}`}>{label}</span>;
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img src={LOGO_URL} alt="Cow Town Redi Mix" />
        <div className="sidebar-logo-text">
          <span>Customer</span>
          <h1>Account Portal</h1>
        </div>
      </div>

      <nav>
        {showAdminNav ? (
          <>
            <div className="nav-section-label">Admin</div>
            {ADMIN_NAV.map(({ to, label, icon, badgeKey }) => {
              const count = badgeKey && adminStats ? adminStats[badgeKey] : 0;
              return (
                <NavLink key={to} to={to} end={to === "/admin"}
                  className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
                  <span style={{ fontSize: "0.9rem" }}>{icon}</span>
                  {label}
                  {count > 0 && <span className="nav-badge">{count}</span>}
                </NavLink>
              );
            })}
            {isSuperAdmin && (
              <>
                <div className="nav-section-label" style={{ marginTop: 12 }}>Super Admin</div>
                <NavLink to="/admin/super"
                  className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
                  <span style={{ fontSize: "0.9rem" }}>⚙</span>
                  Manage Admins
                </NavLink>
              </>
            )}
          </>
        ) : (
          <>
            <NavLink to="/" end className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
              <span style={{ fontSize: "0.9rem" }}>▦</span>
              Dashboard
            </NavLink>

            <div>
              <div
                className={`nav-link ${location.pathname.startsWith("/invoices") ? "active" : ""}`}
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => {
                  if (jobs.length > 0) {
                    setJobsOpen(o => !o);
                    if (!location.pathname.startsWith("/invoices")) navigate("/invoices");
                  } else {
                    navigate("/invoices");
                  }
                }}
              >
                <span style={{ fontSize: "0.9rem" }}>◻</span>
                Invoices
                {jobs.length > 0 && (
                  <span style={{ marginLeft: "auto", fontSize: "0.7rem", opacity: 0.6, transform: jobsOpen ? "rotate(90deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>
                    ▶
                  </span>
                )}
              </div>

              {jobsOpen && jobs.length > 0 && (
                <div style={{ marginLeft: 12, borderLeft: "2px solid rgba(255,255,255,0.15)", paddingLeft: 8, marginBottom: 4 }}>
                  <div
                    className="nav-link"
                    style={{ cursor: "pointer", fontSize: "0.82rem", padding: "6px 10px", color: !selectedJob ? "white" : "rgba(255,255,255,0.6)", fontWeight: !selectedJob ? 500 : 400 }}
                    onClick={handleAllInvoices}
                  >
                    All Jobs
                  </div>
                  {jobs.map(job => (
                    <div
                      key={job.id}
                      className="nav-link"
                      style={{ cursor: "pointer", fontSize: "0.8rem", padding: "5px 10px", color: selectedJob?.id === job.id ? "white" : "rgba(255,255,255,0.6)", fontWeight: selectedJob?.id === job.id ? 500 : 400, background: selectedJob?.id === job.id ? "rgba(255,255,255,0.12)" : undefined }}
                      onClick={() => handleJobClick(job)}
                      title={job.customer_name}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                        {job.customer_name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <NavLink to="/payments" className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
              <span style={{ fontSize: "0.9rem" }}>◈</span>
              Payment History
            </NavLink>
            <NavLink to="/account" className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
              <span style={{ fontSize: "0.9rem" }}>◯</span>
              Account
            </NavLink>
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        {isPreview ? (
          <>
            <div className="sidebar-name" style={{ color: "#ff9999", fontSize: "0.75rem" }}>
              👁 PREVIEW MODE
            </div>
            <div className="sidebar-email">{localStorage.getItem("ar_preview_customer")}</div>
          </>
        ) : (
          <>
            {displayName && (
              <div className="sidebar-name">
                {displayName}
                {getRoleBadge(userRoles)}
              </div>
            )}
            <div className="sidebar-email">{displayEmail}</div>
          </>
        )}
        <button className="btn-logout" onClick={handleLogout}>
          {isPreview ? "Exit Preview" : "Sign out"}
        </button>
      </div>
    </aside>
  );
}
